'use client';

/**
 * DeleteProjectDialog Component
 *
 * Confirmation dialog for permanently deleting a project.
 * Requires user to type the project name for confirmation to prevent accidental deletions.
 */

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Stack,
  Text,
} from '@cortex/ui';
import { AlertTriangle } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

interface DeleteProjectDialogProps {
  open: boolean;
  onClose: () => void;
  project: {
    id: string;
    name: string;
    slug: string;
  };
  onSuccess?: () => void;
}

export function DeleteProjectDialog({
  open,
  onClose,
  project,
  onSuccess,
}: DeleteProjectDialogProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [confirmationText, setConfirmationText] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setConfirmationText('');
      setDeleting(false);
      setError(null);
    }
  }, [open]);

  const isConfirmed = confirmationText === project.name;

  const handleDelete = async () => {
    if (!isConfirmed) return;

    setError(null);
    setDeleting(true);

    try {
      const response = await fetchWithAuth(`/admin/projects/${project.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        if (response.status === 409) {
          const data = await response.json();
          throw new Error(
            data.message ||
              'Cannot delete project with associated requests. Delete or reassign requests first.'
          );
        }
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete project');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Project</DialogTitle>
          <DialogDescription>This action cannot be undone</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Permanent Deletion</AlertTitle>
              <AlertDescription>
                This will permanently delete the project &quot;{project.name}
                &quot; (slug: {project.slug}). This action cannot be undone.
              </AlertDescription>
            </Alert>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Stack gap="xs">
              <Label htmlFor="confirmationText">
                Type{' '}
                <Text as="span" weight="semibold" className="font-mono">
                  {project.name}
                </Text>{' '}
                to confirm
              </Label>
              <Input
                id="confirmationText"
                type="text"
                placeholder={project.name}
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                autoComplete="off"
              />
              <Text color="muted" size="xs" className="mt-1">
                You must type the exact project name to enable deletion
              </Text>
            </Stack>

            {isConfirmed && (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  You are about to permanently delete this project. Make sure
                  all associated requests have been deleted or reassigned first.
                </AlertDescription>
              </Alert>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmed || deleting}
            loading={deleting}
          >
            Delete Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
